import DOMPurify from 'dompurify';
import { supabase } from './supabase';

const extFor = (blob) => {
  const t = (blob.type || '').toLowerCase();
  if (t.includes('png')) return 'png';
  if (t.includes('webp')) return 'webp';
  if (t.includes('svg')) return 'svg';
  return 'jpg';
};

/**
 * Upload an image and return its public URL.
 * card-photos: `<org_id>/<card_id>/<time>.<ext>` · brand-logos: `<org_id>/<time>.<ext>`
 */
export async function uploadImage(bucket, folder, blob) {
  if (!blob.type?.startsWith('image/')) throw new Error('Please choose an image file.');
  if (blob.size > 2 * 1024 * 1024) throw new Error('Images must be under 2 MB.');
  const path = `${folder}/${Date.now()}.${extFor(blob)}`;
  const { data, error } = await supabase.storage.from(bucket).upload(path, blob, {
    cacheControl: '3600',
    contentType: blob.type,
    upsert: false,
  });
  if (error) throw error;
  return supabase.storage.from(bucket).getPublicUrl(data.path).data.publicUrl;
}

/**
 * Delete a file from Storage given its public URL. Best effort: a failure is
 * logged, never shown, because the new image is already saved.
 */
export async function removeStoredImage(bucket, publicUrl) {
  const marker = `/storage/v1/object/public/${bucket}/`;
  const at = (publicUrl || '').indexOf(marker);
  if (at === -1) return;
  const path = decodeURIComponent(publicUrl.slice(at + marker.length).split('?')[0]);
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) console.warn('Could not remove old image:', error.message);
}

/** Shrink an image in the browser before upload (keeps PNG transparency, else JPEG). */
export function resizeImage(file, maxWidth = 800, maxHeight = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = (event) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not open the image.'));
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not resize the image.'))), type, 0.85);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Strip scripts, event handlers and external references from an SVG so it is
 * safe to store and show. Returns a new image/svg+xml Blob.
 */
// Only same-document references (#id) survive in href attributes
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  for (const attr of ['href', 'xlink:href']) {
    const value = node.getAttribute?.(attr);
    if (value != null && !value.trim().startsWith('#')) node.removeAttribute(attr);
  }
});

export async function sanitizeSvg(file) {
  const text = await file.text();
  const clean = DOMPurify.sanitize(text, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'],
    FORBID_TAGS: ['script', 'foreignObject', 'a'],
  });
  if (!/^\s*<svg[\s>]/i.test(clean)) throw new Error('That SVG could not be read. Try exporting it again or use a PNG.');
  return new Blob([clean], { type: 'image/svg+xml' });
}

/** Logos: SVGs are cleaned and kept as vectors; other images are resized. */
export const prepareLogo = (file) =>
  file.type === 'image/svg+xml' ? sanitizeSvg(file) : resizeImage(file, 800, 400);
